import React, { useEffect, useMemo, useState } from 'react';
import { MantineReactTable } from 'mantine-react-table';
import { useTheme } from '../../../ThemeContext'; // Adjust the path as necessary
import { MantineProvider, Modal, Button, Loader } from '@mantine/core';
import { formatDate } from '../../../api/services';
import AddSoil from '../Forms/Soil';
import { getAllSoilPrediction, deleteSoilPrediction } from '../../../api/api';
import { EyeIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../../ConfirmationModal';

export const Soil = () => {
  const { theme } = useTheme(); // Get the theme from context
  const [data, setData] = useState([]);
  const [edit, setEdit] = useState(false);
  const [editData, setEditData] = useState({});
  const [update, setUpdate] = useState(false);
  const [isOpen, setIsOpen] = useState(false); // Manage modal visibility
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [loading, setLoading] = useState(false); // Loader state

  const handleOpen = () => setIsOpen(true); // Open modal
  const handleClose = () => setIsOpen(false); // Close modal

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Set loading to true before fetching
      try {
        const response = await getAllSoilPrediction();
        // console.log(response);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false); // Set loading to false after fetching
    };
    fetchData();
  }, [update]);

  const handleUpdate = () => {
    setUpdate(!update);
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'projectId.project_name',
      header: 'Project Name',
      Cell: ({ cell }) => cell.getValue() || 'N/A',
    },
    {
      accessorKey: 'siteEngineerName',
      header: 'Site Engineer',
    },
    {
      accessorKey: 'qualityInspectorName',
      header: 'Inspector Name',
    },
    {
      accessorKey: 'soilModel',
      header: 'Soil Model Area',
      Cell: ({ cell }) => cell.getValue() || 'N/A',
    },
    {
      accessorKey: 'status',
      header: 'Status',
    },
    {
      accessorKey: 'lastTestDate',
      header: 'Last Test Date',
    },
    {
      accessorKey: 'testResult',
      header: 'Test Result',
    },
    {
      accessorKey: 'comment',
      header: 'Comments',
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      Cell: ({ row }) => (
        <div className="flex space-x-2">
          <PencilIcon
            className="w-5 h-5 text-blue-600 hover:text-blue-800 cursor-pointer"
            onClick={() => handleEditRow(row)}
          />

          <TrashIcon
            className="w-5 h-5 text-red-600 hover:text-red-800 cursor-pointer"
            onClick={() => openDeleteConfirmation(row)}
          />
        </div>
      ),
    },
  ], []);

  const openDeleteConfirmation = (row) => {
    setEditData(row.original);
    setConfirmModalOpen(true);
  };

  const handleDeleteRow = async () => {
    if (editData) {
      try {
        await deleteSoilPrediction(editData._id);
        setData((prevData) => prevData.filter((project) => project._id !== edit._id));
        setConfirmModalOpen(false);
        handleUpdate();
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const handleEditRow = (row) => {
    setEditData(row.original);
    setEdit(true);
  };

  return (
    <MantineProvider
      theme={{
        colorScheme: theme,
        primaryColor: 'blue',
        primaryShade: 5,
      }}
    >
      <div className="flex justify-between p-2 bg-gray-700 items-center text-md">
        <p className="text-lg">Soil Inspection</p>
        {/* Button to trigger modal */}
        <button
          className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto"
          onClick={handleOpen}
        >
          Soil Inspection
        </button>
      </div>
      {/* Show loader while data is being fetched */}
      {loading ? (
        <div className="flex justify-center items-center h-screen">
          <Loader color="blue" size="xl" />
        </div>
      ) : (
        <div>
          {/* Header and Button */}


          {/* Soil Inspection Table */}
          <div className="overflow-x-auto max-h-screen">
            <MantineReactTable
              columns={columns}
              data={data}
              enableColumnActions
              enableColumnFilters
              enablePagination
              enableSorting
              enableRowNumbers
              enableGlobalFilter
              mantineTableProps={{
                highlightOnHover: true,
                withBorder: true,
                sx: {
                  'thead > tr': {
                    backgroundColor: theme === 'dark' ? '#1a202c' : '#f7fafc',
                  },
                  'thead > tr > th': {
                    backgroundColor: theme === 'dark' ? '#2d3748' : '#edf2f7',
                    color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                  },
                  'tbody > tr': {
                    '&:hover': {
                      backgroundColor: theme === 'dark' ? '#2d3748' : '#f1f5f9',
                    },
                  },
                  'tbody > tr > td': {
                    backgroundColor: theme === 'dark' ? '#2d3748' : '#ffffff',
                    color: theme === 'dark' ? '#ffff' : '#333333',
                  },
                },
              }}
            />
          </div>

          {/* Modal for AddSoil form */}

          {/* Confirmation Modal */}
          <ConfirmationModal
            isOpen={confirmModalOpen}
            onClose={() => setConfirmModalOpen(false)}
            onConfirm={handleDeleteRow}
            title="Confirm Deletion"
            message="Are you sure you want to delete this?"
          />
        </div>

      )}
      {isOpen && <AddSoil onClose={handleClose} isOpen={isOpen} editData={editData} edit={edit} handleUpdate={handleUpdate} />}

    </MantineProvider>
  );
};

export default Soil;
